import {createStyles, Group, Text} from '@mantine/core';
import React from 'react';
import {Handle, Node, NodeProps} from 'reactflow';

import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {COMMON_NODE_WIDTH} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';
import {defaultHandleProps} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeHandleUtils';

import {FALLBACK_NODE_HEIGHT} from './ProcedureRunDesignFlowchartNode';
import {getProcedureRunDesignFlowchartNodeHandleId} from './ProcedureRunDesignFlowchartNodeHandle';

export const PROCEDURE_RUN_DESIGN_FLOWCHART_ADD_REPLICATE_NODE_TYPE =
  'procedureRunDesignFlowchartAddReplicateNode';

export interface ProcedureRunDesignFlowchartAddReplicateNodeData {
  /** Method whose column hosts the button. */
  methodId: string;
}

/** Node id for the add-replicate button. */
export function getProcedureRunDesignFlowchartAddReplicateNodeId(methodId: string): string {
  return `add-replicate-${methodId}`;
}

export function createProcedureRunDesignFlowchartAddReplicateNode({
  methodId,
}: {
  methodId: string;
}): Node<ProcedureRunDesignFlowchartAddReplicateNodeData> {
  return {
    id: getProcedureRunDesignFlowchartAddReplicateNodeId(methodId),
    type: PROCEDURE_RUN_DESIGN_FLOWCHART_ADD_REPLICATE_NODE_TYPE,
    data: {methodId},
    position: {x: 0, y: 0},
    selectable: false,
    deletable: false,
    draggable: false,
    connectable: false,
  };
}

export function isProcedureRunDesignFlowchartAddReplicateNode(
  node: Node
): node is Node<ProcedureRunDesignFlowchartAddReplicateNodeData> {
  return node.type === PROCEDURE_RUN_DESIGN_FLOWCHART_ADD_REPLICATE_NODE_TYPE;
}

/** The "Add condition replicate" button node. */
export default function ProcedureRunDesignFlowchartAddReplicateNode(
  props: NodeProps<ProcedureRunDesignFlowchartAddReplicateNodeData>
): JSX.Element {
  const {classes} = useStyles();
  return (
    <div
      className={classes.card}
      {...getTestProps({component: 'ProcedureRunDesignFlowchartAddReplicateNode', element: 'root'})}
    >
      {/* Hidden handle that anchors preview edges. */}
      <Handle
        {...defaultHandleProps('source')}
        id={getProcedureRunDesignFlowchartNodeHandleId(props.id, 'source')}
        isConnectable={false}
        className={classes.hiddenHandle}
      />
      <Group spacing="xs" position="center" align="center" noWrap={true}>
        <BenchlingIcon icon="plus" className={classes.icon} />
        <Text className={classes.label}>Add condition replicate</Text>
      </Group>
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  card: {
    boxSizing: 'border-box',
    width: COMMON_NODE_WIDTH,
    height: FALLBACK_NODE_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // blue[8] is Color.BLUE_500 (#000DB5), matching the label/icon text color.
    border: `1px solid ${theme.colors.blue[8]}`,
    borderRadius: theme.radius.md,
    // blue[0] is Color.BLUE_50 (#F4F5FF), matching the ghost node background.
    backgroundColor: theme.colors.blue[0],
  },
  icon: {
    width: 12,
    height: 12,
    flexShrink: 0,
    color: theme.colors.blue[8],
  },
  label: {
    fontWeight: 600,
    fontSize: theme.fontSizes.sm,
    lineHeight: '16px',
    color: theme.colors.blue[8],
  },
  hiddenHandle: {
    width: 1,
    height: 1,
    minWidth: 1,
    minHeight: 1,
    opacity: 0,
    border: 'none',
    background: 'transparent',
    pointerEvents: 'none',
  },
}));
